import { Check, CheckCircle, FilterLines, Settings01 } from '@untitled-ui/icons-react/build/cjs';
import moment from 'moment';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import OutsideClickHandler from 'src/components/OutsideClickHandler';

import CommonModal from '../CommonModal';
import ConfirmationModalCmp from '../ConfirmModal/ConfirmationModalCmp';
import NotFoundUI from '../NotFoundUI';
import TooltipCmp from '../TooltipCmp';

import NotificationSkeletonLoader from './skeletons/NotificationSkeletonLoader';

interface NotificationProps {
    isLoading: boolean;
    isLoadingMore?: boolean;
    numberOfSkeletonRows?: number;
    notifications: Array<any>;
    handleNotificationClick: (notification: any) => void;
    handleNotificationStatus: (isSingle: boolean, notificationId: number | number[] | null) => void;
    handleClearAll?: () => void;
    notFoundTitle: string;
    notFoundDesc: string;
    handleClose: () => void;
    hasNextPage?: boolean;
    onLoadMore?: () => void;
    timezone?: string;
    activeFilter: FilterType;
    onFilterChange: (filter: FilterType) => void;
    onSettingsClick?: () => void;
    // The customer's assigned sales rep. Shown on every card regardless of who performed the
    // action — the portal always presents activity as coming from the rep.
    salesRepName?: string;
    // Org name from the appearance record (web_title) — leads the rep line, never hardcoded.
    orgName?: string;
}

export type FilterType = 'all' | 'quote' | 'order';

const FILTER_OPTIONS: { label: string; value: FilterType }[] = [
    { label: 'All', value: 'all' },
    { label: 'Quote', value: 'quote' },
    { label: 'Order', value: 'order' },
];

const formatNotificationDate = (date: string | Date | null | undefined, timezone?: string): string => {
    if (!date) return '';

    const tz = timezone || 'America/New_York';
    const momentDate = (moment as any).tz(moment(date), tz);

    return momentDate.format('MMM D [at] hh:mm A');
};

// Renders the description sentence with the quote/order code bolded inline — the code is
// already substituted into the sentence, so it is located and wrapped rather than appended.
const renderDescriptionWithCode = (description: string, code: string) => {
    if (!code || !description.includes(code)) {
        return <span className="text-textSecondary font-normal text-[12px] leading-[18px] text-wrap">{description}</span>;
    }

    const at = description.indexOf(code);

    return (
        <span className="text-textSecondary font-normal text-[12px] leading-[18px] text-wrap">
            {description.slice(0, at)}
            <span className="font-semibold">{code}</span>
            {description.slice(at + code.length)}
        </span>
    );
};

// The notification card: action type + right-aligned time, the description sentence with the
// code bolded inline, then "by <sales rep>". The rep line always names the customer's assigned
// sales rep — never the acting user.
const renderNotificationContent = (notification: any, timezone?: string, salesRepName?: string, orgName?: string) => {
    const dateTime = formatNotificationDate(notification.createdAt || notification.date, timezone);
    const title = notification.title || '';
    const orderId = notification.orderId || '';
    const description = notification.description || '';

    return (
        <>
            <div className="flex flex-row items-center justify-between gap-x-2.5">
                <span className="text-grayLight600 font-normal truncate flex-1 min-w-0 text-[12px] leading-[16px]">{title}</span>
                <span className="text-grayLight600 text-[12px] leading-[16px] font-normal flex-none whitespace-nowrap">{dateTime}</span>
            </div>
            {/* Figma groups the description and the org/rep line 4px apart, with 8px between that group
                and the title row above (supplied by the row's gap-2). Rendered only when at least one of
                the two exists, so an absent body never leaves an 8px gap behind. */}
            {(description || orgName || salesRepName) && (
                <div className="flex flex-col gap-1 w-full">
                    {description && <div className="flex gap-x-1 flex-row items-center">{renderDescriptionWithCode(description, orderId)}</div>}
                    {(orgName || salesRepName) && (
                        <div className="flex gap-x-1.5 flex-row items-center text-gray500 text-[12px] leading-[18px] font-normal pr-6">
                            {orgName && <span className="truncate">{orgName}</span>}
                            {orgName && salesRepName && <span className="w-1 h-1 bg-gray500 rounded-full flex-none"></span>}
                            {salesRepName && <span className="truncate">by {salesRepName}</span>}
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

const NotificationCmp = ({
    isLoading,
    isLoadingMore = false,
    numberOfSkeletonRows = 5,
    notifications,
    handleNotificationClick,
    handleNotificationStatus,
    notFoundTitle,
    notFoundDesc,
    handleClose,
    handleClearAll,
    hasNextPage = false,
    onLoadMore,
    timezone,
    activeFilter,
    onFilterChange,
    onSettingsClick,
    salesRepName,
    orgName,
}: NotificationProps) => {
    const scrollContainerRef = useRef<HTMLUListElement>(null);
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [isConfirmClearOpen, setIsConfirmClearOpen] = useState(false);

    const handleMarkAsRead = (e: React.MouseEvent, notification: any) => {
        e.stopPropagation();
        handleNotificationStatus(true, notification.id);
    };

    const handleScroll = useCallback(() => {
        const container = scrollContainerRef.current;

        if (!container || isLoadingMore || !hasNextPage || !onLoadMore) return;

        const { scrollTop, scrollHeight, clientHeight } = container;

        if (scrollHeight - scrollTop - clientHeight < 50) {
            onLoadMore();
        }
    }, [isLoadingMore, hasNextPage, onLoadMore]);

    useEffect(() => {
        const container = scrollContainerRef.current;

        if (container) {
            container.addEventListener('scroll', handleScroll);

            return () => container.removeEventListener('scroll', handleScroll);
        }
    }, [handleScroll]);

    useEffect(() => {
        if (!isFilterOpen) return;

        const closeOnScroll = () => setIsFilterOpen(false);

        const scrollableContainer = document.querySelector('.main__layout_section');
        const allScrollableContainers = document.querySelectorAll('.overflow-x-auto, .overflow-y-auto, .overflow-auto, .custom-scrollbar-v2');

        scrollableContainer?.addEventListener('scroll', closeOnScroll);
        allScrollableContainers.forEach((el) => el.addEventListener('scroll', closeOnScroll));
        window.addEventListener('scroll', closeOnScroll, true);

        return () => {
            scrollableContainer?.removeEventListener('scroll', closeOnScroll);
            allScrollableContainers.forEach((el) => el.removeEventListener('scroll', closeOnScroll));
            window.removeEventListener('scroll', closeOnScroll, true);
        };
    }, [isFilterOpen]);

    const headerRightContent = (
        <div className="flex items-center gap-x-2">
            <button
                type="button"
                onClick={onSettingsClick}
                className="h-[28px] w-[28px] flex items-center justify-center hover:text-primary text-grayLight600 rounded-md transition-all duration-300 cursor-pointer"
            >
                <Settings01 className="w-4 h-4 " />
            </button>

            <OutsideClickHandler onOutsideClick={() => setIsFilterOpen(false)} containerClassName="relative">
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        setIsFilterOpen((prev) => !prev);
                    }}
                    className={`h-[28px] w-[28px] flex items-center justify-center text-grayLight600 hover:text-primary rounded-md transition-all duration-300 cursor-pointer`}
                >
                    <FilterLines className="w-4 h-4 " />
                </button>

                <ul className={`shadow text-xs rounded-lg bg-white border border-utilityGray200 p-1 my-1.5 z-[1] min-w-[140px] w-fit right-0 absolute leading-[18px] ${isFilterOpen ? '' : 'hidden'}`}>
                    {FILTER_OPTIONS.map((opt) => (
                        <li
                            key={opt.value}
                            className={`text-xs leading-[1.5] font-medium text-grayLight900 rounded-md hover:bg-gray50 px-2 py-1.5 transition-all duration-[0.3s] cursor-pointer flex items-center gap-x-2 ${
                                activeFilter === opt.value ? 'bg-gray50' : 'bg-transparent'
                            }`}
                            onClick={() => {
                                onFilterChange(opt.value);
                                setIsFilterOpen(false);
                            }}
                        >
                            <p className="flex-1">{opt.label}</p>
                            {activeFilter === opt.value && <Check className="w-3 h-3 text-primary flex-none" />}
                        </li>
                    ))}
                </ul>
            </OutsideClickHandler>

            <TooltipCmp message="Mark all as read">
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        setIsConfirmClearOpen(true);
                    }}
                    className="h-[28px] w-[28px] flex items-center justify-center rounded-md transition-all duration-300 cursor-pointer text-grayLight600 hover:text-primary"
                >
                    <CheckCircle className="w-4 h-4" />
                </button>
            </TooltipCmp>
        </div>
    );

    return (
        <>
            <CommonModal
                title={<span className="text-[14px] leading-[20px] font-semibold text-textSecondary">Alerts</span>}
                size={'sm:w-[460px] w-[90%] max-h-[557px] mb-auto mr-[16px] mt-[64px]'}
                handleClose={handleClose}
                modalHeaderClass="!bg-white !p-3 !border-none"
                modalParentClass="!justify-end notification-modal"
                modalHeaderTextClass="!gap-x-1.5"
                modalClassName="flex flex-col"
                hideCloseBtn
                headerRightContent={headerRightContent}
            >
                <ul ref={scrollContainerRef} className="flex-1 flex flex-col overflow-y-auto custom-scrollbar-v2">
                    {isLoading &&
                        Array(numberOfSkeletonRows)
                            .fill(null)
                            .map((_, index) => <NotificationSkeletonLoader key={String(index)} />)}

                    {!isLoading &&
                        notifications.length > 0 &&
                        notifications.map((notification: any) => (
                            <li
                                className={`group flex flex-col cursor-pointer hover:bg-gray50 ${notification.isNew ? 'bg-utilityIndigo25' : 'bg-white'}`}
                                key={notification.id}
                                onClick={() => handleNotificationClick(notification)}
                            >
                                <div className="flex relative items-start px-3 pt-3 pb-3 border-b border-utilityGray200">
                                    <div className="flex items-center pt-1 min-w-[8px] mr-3">{notification.isNew ? <p className="bg-primary w-2 h-2 rounded-[3px]"></p> : ''}</div>

                                    <div className="flex flex-col flex-1 gap-2">
                                        {renderNotificationContent(notification, timezone, salesRepName, orgName)}
                                        {notification.isNew && (
                                            <div className="opacity-0 invisible group-hover:opacity-100 group-hover:visible flex gap-x-1 absolute bottom-4 right-3 transition-opacity duration-150">
                                                <TooltipCmp message="Mark as Read">
                                                    <CheckCircle
                                                        className="text-grayLight600 h-4 w-4 cursor-pointer hover:text-primary700 transition-colors"
                                                        onClick={(e) => handleMarkAsRead(e, notification)}
                                                    />
                                                </TooltipCmp>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </li>
                        ))}

                    {isLoadingMore && <NotificationSkeletonLoader />}
                </ul>

                {!isLoading && !isLoadingMore && notifications.length === 0 && (
                    <NotFoundUI title={notFoundTitle} desc={notFoundDesc} containerClassName="flex-1" iconClassName="!hidden" testId="wal-Claims-dataNotFoundText" />
                )}
            </CommonModal>

            {isConfirmClearOpen && (
                <ConfirmationModalCmp
                    title="Mark all as read"
                    description="Are you sure you want to mark all notifications as read?"
                    submitBtnText="Mark all as read"
                    secondaryBtnText="Cancel"
                    handleClose={() => setIsConfirmClearOpen(false)}
                    handleSecondaryBtn={() => setIsConfirmClearOpen(false)}
                    handleSubmit={() => {
                        setIsConfirmClearOpen(false);
                        handleClearAll?.();
                    }}
                />
            )}
        </>
    );
};

export default NotificationCmp;
